import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Clock,
  TrendingUp,
  Users,
  Vote,
  Building2,
  Mic,
  MicOff,
} from 'lucide-react';

interface MobileSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchSuggestion {
  type: 'deputy' | 'party' | 'vote' | 'topic';
  title: string;
  subtitle?: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const MobileSearchOverlay: React.FC<MobileSearchOverlayProps> = ({
  isOpen,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Focus input when overlay opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const saveToRecentSearches = (searchQuery: string) => {
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const handleSearch = (searchQuery: string = query) => {
    if (searchQuery.trim()) {
      saveToRecentSearches(searchQuery.trim());

      // Smart routing logic (similar to CompactHeader)
      const lowerQuery = searchQuery.toLowerCase();

      // Check for party names
      const parties = ['udi', 'rn', 'evopoli', 'republicanos', 'ps', 'ppd', 'pr', 'dc',
                      'pc', 'frvs', 'comunes', 'cs', 'pdg', 'pli', 'ind'];
      const foundParty = parties.find(party => {
        const regex = new RegExp(`\\b${party}\\b`, 'i');
        return regex.test(searchQuery);
      });

      if (foundParty) {
        navigate(`/partidos?party=${encodeURIComponent(foundParty)}`);
      } else {
        // Default to deputies search for names
        const words = searchQuery.split(' ');
        const isLikelyName = words.length >= 1 && words.every(word =>
          word.length >= 2 && /^[A-ZÁÉÍÓÚÑ]/i.test(word[0]),
        );

        if (isLikelyName) {
          navigate(`/diputados?search=${encodeURIComponent(searchQuery)}`);
        } else {
          navigate(`/votaciones?search=${encodeURIComponent(searchQuery)}`);
        }
      }

      onClose();
    }
  };

  const handleVoiceSearch = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'es-CL';

      recognition.onstart = () => {
        setIsVoiceActive(true);
        if ('vibrate' in navigator) {
          navigator.vibrate(10);
        }
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsVoiceActive(false);
      };

      recognition.onerror = () => {
        setIsVoiceActive(false);
      };

      recognition.onend = () => {
        setIsVoiceActive(false);
      };

      recognition.start();
    } else {
      alert('Voice search not supported in this browser');
    }
  };

  const quickActions: SearchSuggestion[] = [
    {
      type: 'deputy',
      title: 'Buscar Diputados',
      subtitle: 'Por nombre, partido o comuna',
      path: '/diputados',
      icon: Users,
    },
    {
      type: 'vote',
      title: 'Últimas Votaciones',
      subtitle: 'Proyectos de ley recientes',
      path: '/votaciones',
      icon: Vote,
    },
    {
      type: 'party',
      title: 'Análisis de Partidos',
      subtitle: 'Comparar posiciones políticas',
      path: '/partidos',
      icon: Building2,
    },
  ];

  const trendingSearches = [
    'Reforma tributaria',
    'Ley de aborto',
    'Presupuesto 2024',
  ];

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          {/* Search overlay */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="fixed inset-x-0 top-0 z-50 bg-white shadow-xl"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            {/* Search header */}
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  <X className="w-6 h-6" />
                </button>

                <div className="flex-1 relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Buscar..."
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-full focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-base placeholder:text-sm"
                    />

                    {/* Voice search button */}
                    <button
                      onClick={handleVoiceSearch}
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 rounded-full transition-all ${
                        isVoiceActive
                          ? 'bg-red-500 text-white animate-pulse'
                          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                      disabled={isVoiceActive}
                      style={{ minWidth: '36px', minHeight: '36px' }}
                    >
                      {isVoiceActive ? (
                        <MicOff className="w-5 h-5" />
                      ) : (
                        <Mic className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search content */}
            <div className="max-h-screen overflow-y-auto pb-safe-area-inset-bottom">
              {query ? (
                /* Search results would go here */
                <div className="p-4">
                  <p className="text-gray-600 text-center py-8">
                    Presiona Enter para buscar "{query}"
                  </p>
                </div>
              ) : (
                <div className="space-y-6 pb-20">
                  {/* Quick actions */}
                  <div className="px-4 pt-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Acceso rápido</h3>
                    <div className="grid grid-cols-1 gap-3">
                      {quickActions.map((action) => {
                        const Icon = action.icon;
                        return (
                          <motion.button
                            key={action.path}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              navigate(action.path);
                              onClose();
                            }}
                            className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors text-left"
                            style={{ minHeight: '44px' }}
                          >
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                              <Icon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{action.title}</div>
                              <div className="text-sm text-gray-600">{action.subtitle}</div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent searches */}
                  {recentSearches.length > 0 && (
                    <div className="px-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">Búsquedas recientes</h3>
                        <button
                          onClick={clearRecentSearches}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Borrar todo
                        </button>
                      </div>
                      <div className="space-y-2">
                        {recentSearches.map((search, index) => (
                          <motion.button
                            key={index}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleSearch(search)}
                            className="flex items-center w-full p-3 hover:bg-gray-50 rounded-lg transition-colors text-left"
                            style={{ minHeight: '44px' }}
                          >
                            <Clock className="w-5 h-5 text-gray-400 mr-3" />
                            <span className="text-gray-700">{search}</span>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trending searches */}
                  <div className="px-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      <TrendingUp className="inline w-5 h-5 mr-2" />
                      Temas populares
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {trendingSearches.map((trend, index) => (
                        <motion.button
                          key={index}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleSearch(trend)}
                          className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors"
                          style={{ minHeight: '36px' }}
                        >
                          {trend}
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Voice search hint */}
                  {isVoiceActive && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="px-4 py-8 text-center"
                    >
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Mic className="w-8 h-8 text-red-600 animate-pulse" />
                      </div>
                      <p className="text-gray-600 font-medium">Escuchando...</p>
                      <p className="text-sm text-gray-500 mt-1">Di lo que quieres buscar</p>
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};